// Shapes in this file follow docs/api-contract.md. Keep them in sync with it.

// ---------------------------------------------------------------------------
// Auth / user
// ---------------------------------------------------------------------------

export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  firstName: string; // For frontend consistency
  lastName: string; // For frontend consistency
  is_verified: boolean;
  /** Staff may open the job-sources page. Read-only; set on the server. */
  is_staff: boolean;
  subscription_tier: 'free' | 'pro' | 'elite';
  created_at: string;
  profile?: Record<string, unknown>;
}

export interface AuthTokens {
  access: string;
  refresh: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirm: string;
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export interface Profile {
  first_name: string;
  last_name: string;
  preferred_name: string;
  email: string;
  phone: string;
  phone_country_code: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  current_employer: string;
  current_title: string;
  years_experience: number | null;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  website_url: string;
  preferred_roles: string[];
  preferred_locations: string[];
  preferred_tech_stack: string[];
  preferred_salary_min: number | null;
  preferred_salary_max: number | null;
}

/** Fields of Profile the user may PATCH (email is read-only). */
export type ProfilePatch = Partial<Omit<Profile, 'email'>>;

export interface Education {
  id: number | string;
  school: string;
  degree: string;
  field_of_study: string;
  start_date: string | null;
  end_date: string | null;
  gpa: string | number | null;
  gpa_scale: string | number | null;
  ordering: number;
}

export interface Experience {
  id: number | string;
  employer: string;
  title: string;
  location: string;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean;
  description: string;
  ordering: number;
}

export type SkillCategory = 'technical' | 'soft' | 'language' | 'tool' | 'other';

export const SKILL_CATEGORIES: { value: SkillCategory; label: string }[] = [
  { value: 'technical', label: 'Technical' },
  { value: 'tool', label: 'Tool' },
  { value: 'language', label: 'Language' },
  { value: 'soft', label: 'Soft skill' },
  { value: 'other', label: 'Other' },
];

export interface Skill {
  id: number | string;
  name: string;
  category: SkillCategory;
  years: number | null;
  ordering: number;
}

export interface Certification {
  id: number | string;
  name: string;
  issuer: string;
  issued_on: string | null;
  expires_on: string | null;
  credential_id: string;
}

export interface ResumeSummary {
  id: string;
  label: string;
  filename: string;
  content_type: string;
  size: number;
  is_default: boolean;
}

export interface CustomAnswer {
  id: number;
  prompt: string;
  normalized_prompt: string;
  value: string;
}

export interface ProfileSnapshot {
  taxonomy_version: number;
  profile: Profile & { full_name: string; location: string };
  education: Education[];
  experience: Experience[];
  skills: Skill[];
  certifications: Certification[];
  answers: Record<string, { value: AnswerValue; verified: boolean }>;
  custom_answers: CustomAnswer[];
  resumes: ResumeSummary[];
}

// ---------------------------------------------------------------------------
// Answers (canonical questions)
// ---------------------------------------------------------------------------

export type AnswerValue = string | number | boolean | null;

export type QuestionValueType =
  | 'text'
  | 'email'
  | 'tel'
  | 'url'
  | 'date'
  | 'month'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'money'
  | 'long_text'
  | 'file';

export type QuestionGroup =
  | 'personal'
  | 'contact'
  | 'address'
  | 'links'
  | 'employment'
  | 'education'
  | 'experience'
  | 'eligibility'
  | 'preferences'
  | 'compensation'
  | 'availability'
  | 'eeo'
  | 'documents'
  | 'misc'
  | 'prompt';

export interface CanonicalQuestion {
  key: string;
  group: QuestionGroup | string;
  label: string;
  value_type: QuestionValueType;
  options: string[] | null;
  sensitivity: 'personal' | 'sensitive' | 'protected';
  fill_policy: 'always' | 'verified_only';
  help_text: string;
}

export interface UserAnswer {
  question_key: string;
  value: AnswerValue;
  verified: boolean;
  source: string;
  last_used: string | null;
  use_count: number;
  updated_at: string;
}

// Free-text custom answers live on the templates app with category
// `custom_response`.
export interface FormTemplate {
  id: number;
  category: string;
  question: string;
  value: string;
  use_count: number;
  last_used: string | null;
}

export const CUSTOM_RESPONSE_CATEGORY = 'custom_response';

/**
 * `GET /answers/common/`: one question employers on the job board ask, and
 * whether the user has an answer for it. `question_key` names the canonical
 * question; null means a custom wording, answered as a custom answer whose
 * prompt is `label`.
 */
export interface CommonQuestion {
  id: string;
  label: string;
  kind: 'standard' | 'custom';
  /** Active jobs that ask it, and how many of those require it. */
  jobs: number;
  required_jobs: number;
  answered: boolean;
  question_key: string | null;
}

// ---------------------------------------------------------------------------
// Resumes
// ---------------------------------------------------------------------------

export interface Resume {
  id: string;
  label: string;
  filename: string;
  content_type: string;
  size: number;
  is_default: boolean;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

export interface Company {
  id: string;
  name: string;
  domain: string;
  logo_url: string;
  description: string;
  employee_count: string;
  founded_year: number | null;
  created_at: string;
  updated_at: string;
}

export type WorkplaceType = 'remote' | 'hybrid' | 'onsite';

export type ExperienceLevel =
  | 'intern'
  | 'entry'
  | 'mid'
  | 'senior'
  | 'lead'
  | 'manager'
  | 'director'
  | 'executive';

export type JobCategory =
  | 'engineering'
  | 'hardware'
  | 'data'
  | 'science'
  | 'healthcare'
  | 'consulting'
  | 'security'
  | 'it'
  | 'design'
  | 'product'
  | 'marketing'
  | 'sales'
  | 'customer_success'
  | 'people'
  | 'finance'
  | 'legal'
  | 'operations'
  | 'other';

export type JobType = 'full_time' | 'part_time' | 'contract' | 'internship';

export type JobSort = 'newest' | 'oldest' | 'salary';

export interface JobCompany {
  id: string;
  name: string;
  logo_url: string;
  /** A staffing agency or job marketplace rather than the employer itself. */
  is_staffing_agency: boolean;
}

/**
 * How many of a job application's required questions the signed-in user
 * already has answers for. `ready` when every one of them is answered.
 */
export interface JobCoverage {
  required: number;
  answered: number;
  ready: boolean;
}

export type JobQuestionKind = 'standard' | 'custom' | 'eeo' | 'file';

/** One question on an application form, as `GET /jobs/{id}/` describes it. */
export interface JobQuestion {
  label: string;
  kind: JobQuestionKind;
  required: boolean;
  answered: boolean;
}

/** The application form's questions, each marked answered or not. */
export interface JobQuestions {
  required: number;
  answered: number;
  ready: boolean;
  /** Labels of required questions still unanswered, at most six. */
  missing: string[];
  total: number;
  questions: JobQuestion[];
}

/** A coverage filter: every required question answered, or all but two. */
export type CoverageLevel = 'ready' | 'near';

/** The signed-in user's application state carried on every job. */
export interface JobApplicationState {
  /** null when the user has no application for the job (or is signed out). */
  application_status: ApplicationStatus | null;
  /** For PATCH / DELETE /applications/{id}/. */
  application_id: string | null;
  is_saved: boolean;
}

/** `JobListItem` in the contract: one job on the board. */
export interface Job extends JobApplicationState {
  id: string;
  title: string;
  company: JobCompany;
  location: string;
  /** ISO 3166-1 alpha-2, or '' when it could not be derived. */
  country: string;
  workplace_type: WorkplaceType | '';
  is_remote: boolean;
  job_type: JobType;
  experience_level: ExperienceLevel | '';
  category: JobCategory | '';
  department: string;
  tech_stack: string[];
  min_years_experience: number | null;
  /** Yearly amounts. */
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  /** How the employer stated the salary; the amounts are annualised. */
  salary_interval: string;
  /** Ready to show, e.g. "$165K - $190K"; '' when not stated. */
  salary_display: string;
  posted_at: string;
  source_platform: string;
  source_url: string;
  /** The employer's application form, where the extension fills it. */
  application_url: string;
  company_careers_url: string;
  /** Plain-text teaser, about 600 characters at most. */
  snippet: string;
  /**
   * null when signed out, or when the application form has not been read yet
   * (or the ATS does not publish it, e.g. Lever).
   */
  coverage: JobCoverage | null;
  /**
   * How the signed-in person matches the job, as its card shows it: from what
   * the posting asks for once it has been read ('posting'), else from the
   * board's technology tags ('tags'). null when signed out, when the person
   * has written nothing, or when the job names nothing.
   */
  match?: { score: number; have: number; counted: number; read: 'posting' | 'tags' } | null;
}

export interface JobUserApplication {
  id: string;
  status: ApplicationStatus;
  applied_at: string | null;
  notes: string;
}

/** `GET /jobs/{id}/`. `description` is plain text, never HTML. */
export interface JobDetail extends Job {
  description: string;
  /** false when the live read from the ATS failed and this is the teaser. */
  description_is_full: boolean;
  /** false once the listing has left the employer's board. */
  is_active: boolean;
  last_scraped_at: string;
  user_application: JobUserApplication | null;
  /** null when signed out, or when the form has not been read (see `coverage`). */
  questions: JobQuestions | null;
}

/**
 * Query parameters of `GET /jobs/` and `GET /jobs/facets/`. Plural filters are
 * comma-separated lists that match any of the values.
 */
export interface JobFilters {
  q?: string;
  location?: string;
  workplace?: string;
  level?: string;
  category?: string;
  job_type?: string;
  country?: string;
  ats?: string;
  company?: string;
  tech?: string;
  salary_min?: number;
  has_salary?: 1;
  max_years?: number;
  /** Days. */
  posted_within?: number;
  hide_applied?: 1;
  saved?: 1;
  /** Drops listings from staffing agencies and job marketplaces. Works signed out. */
  hide_agencies?: 1;
  /** Signed-in only; ignored when signed out. */
  coverage?: CoverageLevel;
  sort?: JobSort;
  page?: number;
  page_size?: number;
}

/** Counts over the other current filters. `near` includes `ready`. */
export interface CoverageFacets {
  ready: number;
  near: number;
  /** Jobs whose application form has been read. */
  known: number;
}

export interface FacetOption {
  value: string;
  count: number;
}

/** `GET /jobs/facets/`: each group is counted with the other filters applied. */
export interface JobFacets {
  workplace: FacetOption[];
  level: FacetOption[];
  category: FacetOption[];
  job_type: FacetOption[];
  country: FacetOption[];
  /** Staff only: which hiring system the jobs came from. */
  ats?: FacetOption[];
  company: FacetOption[];
  tech: FacetOption[];
  total: number;
  with_salary: number;
  /**
   * Matching listings that come from staffing agencies, counted with the
   * agency filter itself left out, so it does not move when that is toggled.
   */
  staffing_agency_jobs: number;
  boards: number;
  last_updated: string | null;
  /** Absent when signed out. */
  coverage?: CoverageFacets;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Job sources (staff only)
// ---------------------------------------------------------------------------

export type AtsPlatform = 'greenhouse' | 'lever' | 'ashby' | 'workable' | 'gem' | 'rippling' | 'jazzhr' | 'recruitee' | 'jobvite' | 'breezy';

/** How the job board is kept fresh. */
export interface IngestSettings {
  /** Stops scheduled refreshes only; "run now" and per-board refresh still work. */
  paused: boolean;
  /** 1..336. A board is due again this long after its last refresh. */
  refresh_interval_hours: number;
  enabled_platforms: AtsPlatform[];
  /** 0..2000; 0 means no cap. */
  max_boards_per_run: number;
  /** 1..50 consecutive failed reads before a board is switched off. */
  retire_after_failures: number;
  updated_at: string;
}

export type IngestSettingsPatch = Partial<Omit<IngestSettings, 'updated_at'>>;

export type IngestTrigger = 'schedule' | 'manual' | 'command';

/** One refresh pass. created / updated / deactivated count jobs. */
export interface IngestRun {
  id: number | string;
  started_at: string;
  seconds: number;
  trigger: IngestTrigger;
  boards: number;
  failed: number;
  created: number;
  updated: number;
  deactivated: number;
  /** Boards still due afterwards. */
  remaining: number;
}

export interface JobSourcesTotals {
  active_jobs: number;
  boards: number;
  active_boards: number;
  due_boards: number;
  failing_boards: number;
  retired_boards: number;
  last_refreshed_at: string | null;
  stalest_board_at: string | null;
  /** Active jobs whose application form has been read. */
  jobs_with_questions: number;
  /** Active jobs on an ATS that publishes the form, not read yet. */
  jobs_awaiting_questions: number;
  /** Distinct forms: jobs asking the same questions share one. */
  question_sets: number;
  /** Boards of companies flagged as staffing agencies or job marketplaces. */
  agency_boards: number;
  /** Active jobs from those companies. */
  agency_jobs: number;
}

export interface JobSourcesPlatform {
  platform: AtsPlatform;
  enabled: boolean;
  boards: number;
  active_boards: number;
  jobs: number;
}

/** `GET /jobs/admin/overview/`. */
export interface JobSourcesOverview {
  settings: IngestSettings;
  totals: JobSourcesTotals;
  platforms: JobSourcesPlatform[];
  /** Newest first, up to 15. */
  recent_runs: IngestRun[];
}

export type BoardDiscovery = 'seed' | 'manual' | 'extension';

/** Who decided the agency flag: a refresh ('auto') or staff ('manual'); '' before either. */
export type AgencySource = '' | 'auto' | 'manual';

/** One company board in the registry. */
export interface SourceBoard {
  id: string;
  platform: AtsPlatform;
  board_token: string;
  company_name: string;
  careers_url: string;
  is_active: boolean;
  job_count: number;
  last_scraped_at: string | null;
  consecutive_failures: number;
  last_error: string;
  /** null uses the global interval. */
  refresh_interval_hours: number | null;
  discovered_via: BoardDiscovery | string;
  created_at: string;
  is_due: boolean;
  /** The company's flag, shared by all of its boards. */
  is_staffing_agency: boolean;
  agency_source: AgencySource;
  /** Why it was flagged, e.g. "postings speak for a client" or "set by staff". */
  agency_reason: string;
}

export type BoardStateFilter = 'active' | 'off' | 'failing' | 'due' | 'never';

/** Staffing agencies only ('yes') or direct employers only ('no'). */
export type BoardAgencyFilter = 'yes' | 'no';

export type BoardSort = 'name' | 'jobs' | 'oldest' | 'newest';

/** Query parameters of `GET /jobs/admin/boards/`. */
export interface BoardListParams {
  q?: string;
  platform?: AtsPlatform;
  agency?: BoardAgencyFilter;
  state?: BoardStateFilter;
  sort?: BoardSort;
  page?: number;
  page_size?: number;
}

/** What reading one board did. `error` is '' when the read worked. */
export interface BoardReadResult {
  created: number;
  updated: number;
  deactivated: number;
  active: number;
  error: string;
}

export interface NewBoard {
  platform: AtsPlatform;
  token: string;
  name?: string;
  ingest?: boolean;
}

export interface BoardPatch {
  is_active?: boolean;
  refresh_interval_hours?: number | null;
  /** A manual decision: later refreshes keep it. */
  is_staffing_agency?: boolean;
}

/** `POST /jobs/admin/run/`: one call of up to about 40 seconds. */
export interface IngestRunResult {
  boards: number;
  failed: number;
  created: number;
  updated: number;
  deactivated: number;
  active: number;
  /** Boards still to read; call again while this is above zero. */
  remaining: number;
  /** Boards too slow to read in a call this short; the scheduled refresh reads them. Not counted in `remaining`. */
  deferred?: number;
  seconds: number;
  finished_at: string;
  /** Set for "refresh everything": send it back to continue the same sweep. */
  before?: string | null;
}

// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------

export type ApplicationStatus =
  | 'saved'
  | 'in_progress'
  | 'applied'
  | 'interview_scheduled'
  | 'interviewed'
  | 'offer_received'
  | 'offer_accepted'
  | 'rejected'
  | 'withdrawn';

export interface ApplicationStatusMeta {
  value: ApplicationStatus;
  label: string;
  color: string;
}

/** All nine statuses in pipeline order; rejected/withdrawn are terminal. */
export const APPLICATION_STATUSES: ApplicationStatusMeta[] = [
  { value: 'saved', label: 'Saved', color: '#9e9e9e' },
  { value: 'in_progress', label: 'In progress', color: '#607d8b' },
  { value: 'applied', label: 'Applied', color: '#2196f3' },
  { value: 'interview_scheduled', label: 'Interview scheduled', color: '#ff9800' },
  { value: 'interviewed', label: 'Interviewed', color: '#9c27b0' },
  { value: 'offer_received', label: 'Offer received', color: '#4caf50' },
  { value: 'offer_accepted', label: 'Offer accepted', color: '#2e7d32' },
  { value: 'rejected', label: 'Rejected', color: '#f44336' },
  { value: 'withdrawn', label: 'Withdrawn', color: '#795548' },
];

export const statusMeta = (status: string): ApplicationStatusMeta =>
  APPLICATION_STATUSES.find((s) => s.value === status) ?? {
    value: status as ApplicationStatus,
    label: status,
    color: '#9e9e9e',
  };

export interface ApplicationJobSummary {
  id: string;
  title: string;
  company: { id: string; name: string; logo_url: string };
  location: string;
  application_url: string;
  source_platform: string;
  salary_display: string;
}

export interface ApplicationListItem {
  id: string;
  status: ApplicationStatus;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
  ats_platform: string;
  interview_stage: string;
  next_action_at: string | null;
  notes_preview: string;
  job: ApplicationJobSummary;
  resume: { id: string; label: string } | null;
}

export type FilledBy = 'auto' | 'user' | 'skipped';

export interface ApplicationAnswer {
  id: number | string;
  question_key: string;
  prompt_text: string;
  value: string;
  filled_by: FilledBy;
}

export interface ApplicationEvent {
  id: number | string;
  event_type: string;
  description: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface ApplicationDetail extends ApplicationListItem {
  notes: string;
  cover_letter: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  applied_via_extension: boolean;
  job: ApplicationJobSummary & { description: string; source_url: string };
  answers: ApplicationAnswer[];
  events: ApplicationEvent[];
}

export interface ApplicationPatch {
  status?: ApplicationStatus;
  notes?: string;
  interview_stage?: string;
  next_action_at?: string | null;
  applied_at?: string | null;
  resume_id?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_currency?: string | null;
}

export type ApplicationBoard = Record<ApplicationStatus, ApplicationListItem[]>;

export interface ApplicationStats {
  total: number;
  by_status: Partial<Record<ApplicationStatus, number>>;
  applied_last_7_days: number;
  applied_last_30_days: number;
  response_rate: number;
  interview_rate: number;
  offer_rate: number;
  recent: ApplicationListItem[];
}

// ---------------------------------------------------------------------------
// Staff: what the service costs (`/ops/costs/`)
// ---------------------------------------------------------------------------

/** Why a cost source could not be read. */
export interface CostUnavailable {
  available: false;
  reason: 'not_ready' | 'not_permitted' | 'not_configured' | 'error';
  detail: string;
}

export interface AwsMonthCost {
  /** "2026-10" */
  month: string;
  /** Charges before credits. */
  usage: number;
  /** Credits and refunds; zero or negative. */
  credits: number;
  /** What is actually billed: usage plus credits. */
  billed: number;
  services: { name: string; amount: number }[];
}

export interface AwsCosts {
  available: true;
  fetched_at: string;
  currency: string;
  months: AwsMonthCost[];
  this_month: AwsMonthCost;
  /** Usage still expected this month, on top of `this_month`; null when AWS cannot forecast yet. */
  forecast_rest_of_month: number | null;
  daily: { day: string; amount: number }[];
}

export interface NeonUsage {
  available: true;
  period_start: string | null;
  period_end: string | null;
  compute_hours: number;
  active_hours: number;
  storage_gb_months: number;
  written_gb: number;
  transfer_gb: number;
  logical_limit_mb: number | null;
  estimated_cost: number | null;
  rates: { compute_hour?: number; gb_month?: number };
}

export interface DatabaseCosts {
  engine: string;
  size_mb: number | null;
  tables: { name: string; size_mb: number; rows: number }[];
  /** Ingestion stops adding jobs above this size; null when there is no limit. */
  ingest_limit_mb: number | null;
  neon: NeonUsage | CostUnavailable | null;
}

export interface UsageTotals {
  today: number;
  this_month: number;
  all_time: number;
}

export interface AiCosts {
  this_month: { cost: number; calls: number; input_tokens: number; cached_input_tokens: number; output_tokens: number };
  /** The part of this month's cost that is already on the AWS bill (calls through Bedrock). */
  on_aws_bill: number;
  by_feature: { name: string; cost: number; calls: number }[];
  by_model: { name: string; cost: number; calls: number }[];
  months: { month: string; cost: number; calls: number }[];
  tailored_resumes: {
    generated: UsageTotals;
    submitted: UsageTotals;
    cost_this_month: number;
    cost_all_time: number;
    cost_per_resume: number | null;
  };
}

export interface ServiceCostsSummary {
  this_month: number;
  months: { month: string; amount: number }[];
  count: number;
}

export interface CostPart {
  key: 'aws' | 'database' | 'ai' | 'services';
  label: string;
  /** Null when the source could not be read. */
  amount: number | null;
  note: string;
}

export interface CostOverview {
  month: string;
  currency: string;
  total_known: number;
  /** False while a source cannot be read: the total is then a lower bound. */
  complete: boolean;
  parts: CostPart[];
  aws: AwsCosts | CostUnavailable;
  database: DatabaseCosts;
  ai: AiCosts;
  services: ServiceCostsSummary;
}

export type ServiceCostPeriod = 'monthly' | 'yearly' | 'one_off';

/** A cost entered by hand (`/ops/costs/services/`). */
export interface ServiceCost {
  id: number;
  name: string;
  vendor: string;
  /** Decimal as text, e.g. "12.00". */
  amount: string;
  currency: string;
  period: ServiceCostPeriod;
  started_on: string;
  ended_on: string | null;
  notes: string;
}

export type ServiceCostInput = Omit<ServiceCost, 'id' | 'currency'>;

export interface AiPrice {
  vendor: string;
  model: string;
  /** US dollars per million tokens. */
  input: number;
  cached_input: number | null;
  output: number;
  source?: string;
  note?: string;
}

export interface AiPriceTable {
  as_of: string;
  note: string;
  models: AiPrice[];
}

// ---------------------------------------------------------------------------
// Application status tracking by email (`/mail/`)
// ---------------------------------------------------------------------------

export type EmailTrackingMode = 'off' | 'alias' | 'copy';

export interface EmailTrackingSettings {
  mode: EmailTrackingMode;
  /** The user's private JobFlo address. Permanent. */
  address: string;
  /** Where direct mail is forwarded: always the account's own email. */
  forwards_to: string;
  /** False until JobFlo can forward mail to any address; the alias mode needs it. */
  alias_available: boolean;
  /** Whether the account's email is confirmed as the user's own; mail is forwarded only then. */
  email_confirmed: boolean;
  /** '' when the plan includes email tracking, else why not. */
  plan_allows: string;
  mode_changed_at: string | null;
}

export interface EmailTrackingSetup {
  address: string;
  mode: EmailTrackingMode;
  /** The mailbox provider's "confirm this forwarding address" message, when one has arrived. */
  confirmation: { provider: string; code: string; confirm_url: string; mailbox: string; received_at: string } | null;
  copies_received: number;
  last_copy_at: string | null;
  sender_domains: string[];
  /** What goes in the "From" box of a Gmail filter. */
  gmail_filter_from: string;
}

export type MailClassification = 'received' | 'interview' | 'assessment' | 'rejected' | 'offer' | 'other' | 'verification';

export interface InboundMessage {
  id: string;
  application: { id: string; job_title: string; company: string; status: ApplicationStatus } | null;
  direction: 'direct' | 'copy';
  sender: string;
  sender_name: string;
  subject: string;
  received_at: string;
  classification: MailClassification;
  confidence: number;
  classified_by: 'rules' | 'model' | 'user';
  matched_by: string;
  excerpt: string;
  forwarded_at: string | null;
  forward_error: string;
  /** The application status this message set, when it set one. */
  status_applied: string;
}

export interface InboundMessageList {
  results: InboundMessage[];
  total: number;
  unmatched: number;
}

// ---------------------------------------------------------------------------
// Language models for tailored resumes (`/ai/`)
// ---------------------------------------------------------------------------

export interface ModelVendor {
  key: string;
  label: string;
  /** Where a user creates a key with this vendor. */
  keys_url: string;
  has_key: boolean;
  /** The last four characters of the stored key; the key itself is never sent back. */
  last4: string;
}

export interface ModelPreference {
  source: 'jobflo' | 'own';
  vendor: string;
  model: string;
}

export interface ModelOptions {
  vendors: ModelVendor[];
  preference: ModelPreference;
  jobflo: { available: boolean; vendor: string; model: string; estimate: number | null };
  /** The resume the estimates are worked out from; null when none is uploaded. */
  resume: { id: string; label: string; readable: boolean; tokens: number } | null;
  /** What one tailored resume takes for that resume. */
  tokens: { input: number; output: number };
}

export interface VendorModel {
  id: string;
  /** US dollars per million tokens; null while the price is not known. */
  input: number | null;
  output: number | null;
  /** US dollars for one tailored resume of this user's; null without a price or a readable resume. */
  estimate: number | null;
}

/** A resume tailored for one job (`/resumes/tailored/`). */
export interface TailoredVersion {
  id: string;
  status: 'building' | 'ready' | 'failed';
  /** What the build is doing, while it is building. */
  step_label: string;
  error: string;
  job_title: string;
  company_name: string;
  job_url: string;
  job: string | null;
  application: string | null;
  submitted_at: string | null;
  /** When the application went further than a submission; such a resume is kept and flagged. */
  advanced_at: string | null;
  /** The furthest status the application reached ('' until it went past a submission, or was rejected). */
  outcome: string;
  filename: string;
  model: string;
  /** 'jobflo' or 'own' (built with the user's own key). */
  key_source: string;
  cost_usd: number;
  seconds: number;
  /** How the bullets fared: kept as they were, reworded, or put back by the fact check. */
  summary: { bullets: number; kept: number; reworded: number; restored: number; dropped: number };
  created_at: string;
  finished_at: string | null;
  /** When the person last changed it themselves; null if they never did. */
  edited_at?: string | null;
  /**
   * What was rewritten (detail only). `for` is the job requirement a line
   * answers ('' when none was named); `by` says whose wording it is.
   */
  changes?: { kind?: 'summary' | 'line'; before: string; after: string; for?: string; by?: 'you' | 'jobflo'; library?: boolean }[];
  /** The resume as the editor shows it (detail only; null when it cannot be edited). */
  document?: TailoredDocument | null;
}

/** One thing in the Experience library: something written down, or the text of a document. */
export interface LibraryEntry {
  id: string;
  kind: 'note' | 'document';
  title: string;
  /** The employer it is about, as the resume names it; '' when it is not about one job. */
  employer: string;
  filename: string;
  excerpt: string;
  length: number;
  /** How many things done were found in it; null for a document not read yet. */
  lines: number | null;
  created_at: string;
  updated_at: string;
  /** The whole text (detail only). */
  text?: string;
}

export interface Library {
  entries: LibraryEntry[];
  /** The jobs an entry can be about. */
  jobs: { employer: string; title: string }[];
  limit: number;
}

/** How the signed-in person matches a job (`/jobs/<id>/match/`). */
export interface JobMatch {
  /** 0 to 100; null when there is nothing to compare. */
  score: number | null;
  have: { term: string; must: boolean }[];
  gaps: { term: string; must: boolean }[];
  counted: number;
  sources: { resume: boolean; library: boolean; profile: boolean };
  /** 'posting' when the posting itself was read; 'tags' when only the board's technology tags were available. */
  read: 'posting' | 'tags';
  /** The posting is being read in the background; asking again shortly gives the fuller answer. */
  reading?: boolean;
  must_have: string[];
}

/** The parts of a tailored resume a person can change, under the headings they cannot. */
export interface TailoredDocument {
  summary: string;
  experience: { id: string; title: string; employer: string; dates: string; bullets: string[] }[];
  projects: { id: string; name: string; bullets: string[] }[];
  skills: string[];
}

export interface TailoredEdit {
  summary: string;
  experience: { id: string; bullets: string[] }[];
  projects: { id: string; bullets: string[] }[];
  skills: string[];
}

// ---------------------------------------------------------------------------
// Staff: provider keys (`/ops/keys/`). Write-only: no key is ever sent to the browser.
// ---------------------------------------------------------------------------

export interface ProviderKey {
  name: string;
  label: string;
  vendor: string;
  set: boolean;
  last4: string;
  changed_at: string | null;
  changed_by: string;
}

export interface ProviderKeys {
  /** False on a server with no keys secret (local development). */
  available: boolean;
  keys: ProviderKey[];
  changes: { name: string; action: 'set' | 'cleared'; by: string; at: string }[];
}

/** How the owner proves again who they are before a key changes. */
export type KeyConfirmation = { password: string } | { google_credential: string };

// ---------------------------------------------------------------------------
// Staff: the instructions given to the models (`/ops/prompts/`).
// ---------------------------------------------------------------------------

export interface ModelPrompt {
  name: string;
  label: string;
  /** The text in use. */
  text: string;
  builtin_text: string;
  /** True when the owner's own text is the one in use. */
  custom: boolean;
  /** Why a stored text is not being used ('' when there is none or it is in use). */
  ignored_because: string;
  changed_at: string | null;
  changed_by: string;
}

export interface ModelPrompts {
  /** False on a server with no prompts secret: the built-in texts are used and cannot be changed. */
  available: boolean;
  prompts: ModelPrompt[];
}

// ---------------------------------------------------------------------------
// Saved searches (`/jobs/searches/`)
// ---------------------------------------------------------------------------

export type SavedSearchAlert = 'off' | 'daily' | 'weekly';

export interface SavedSearch {
  id: string;
  name: string;
  /** The board's query string without `page` or `job`, pairs sorted; `/jobs?` + this opens it. */
  query: string;
  /** Email the new matches as a digest. */
  alert: SavedSearchAlert;
  created_at: string;
  last_used_at: string | null;
}

// ---------------------------------------------------------------------------
// Plans (`/subscriptions/plan/`)
// ---------------------------------------------------------------------------

export type PlanKey = 'free' | 'trial' | 'pro';

export interface PlanLimits {
  /** Tailored resumes a month; 0 means none. */
  tailored_per_month: number;
  email_tracking: boolean;
  /** "How you match" on jobs. */
  job_match?: boolean;
}

export interface PlanState {
  plan: PlanKey;
  label: string;
  trial: boolean;
  /** When the trial or paid plan ends; null for Free and for staff. */
  valid_until: string | null;
  /** A trial or paid plan ran out and the account is on Free now. */
  lapsed: boolean;
  lapsed_from: string;
  limits: PlanLimits;
  usage: { tailored_this_month: number };
  /** The plans on offer: Free and Pro. */
  plans: ({ key: PlanKey; label: string } & PlanLimits)[];
  /** What Pro costs; an amount is null until the owner has set it. */
  prices: { currency: string; monthly: string | null; yearly: string | null; yearly_saving_percent: number | null };
  /** What a new account's trial gives. */
  trial_offer: { days: number; tailored: number };
}

/** Staff: the numbers of the plans (`/ops/plans/`). */
export interface PlanSettings {
  pro_price_monthly: string | null;
  pro_price_yearly: string | null;
  pro_tailored_per_month: number;
  trial_days: number;
  trial_tailored: number;
  yearly_saving_percent: number | null;
  updated_at: string | null;
  updated_by: string;
}

